/**
 * Organization service layer for Firebase callable functions
 * Provides type-safe organization management API with proper error handling
 */
import { createCallable } from './firebase';
import { 
  OnboardingStatusResponse,
  CreateOrganizationInput,
  CreateOrganizationResponse,
  JoinOrganizationInput,
  JoinOrganizationResponse,
  Organization,
  CreateInviteCodeInput,
  CreateInviteCodeResponse,
  SwitchOrganizationInput,
  SwitchOrganizationResponse,
  UserOrganizationMembership
} from '../types/organization';

// API error class for better error handling
export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic API call wrapper with error handling
async function apiCall<T, R>(
  functionName: string,
  data?: T
): Promise<R> {
  try {
    const callable = createCallable<T, R>(functionName);
    const result = await callable(data);
    
    // Firebase callable functions return the data directly
    return result.data;
  } catch (error: any) {
    console.error(`Error calling ${functionName}:`, error);
    
    // Handle Firebase function errors
    if (error.code) {
      throw new ApiError(
        error.message || `Firebase error: ${error.code}`,
        error.code,
        error
      );
    }
    
    // Network or other errors
    throw new ApiError(
      error.message || 'Network error occurred',
      'NETWORK_ERROR',
      error
    );
  }
}

// Organization API functions
export const organizationService = {
  /**
   * Check onboarding status for the current user
   * Determines if user has an organization and if onboarding is needed
   */
  async checkOnboardingStatus(): Promise<OnboardingStatusResponse> {
    console.log('[Organization API] Checking onboarding status...');
    try {
      const response = await apiCall<void, OnboardingStatusResponse>('checkOnboardingStatus');
      console.log('[Organization API] Onboarding status response:', response);
      return response;
    } catch (error) {
      console.error('[Organization API] Failed to check onboarding status:', error);
      throw error;
    }
  },

  /**
   * Create a new organization (user becomes owner)
   */
  async createOrganization(input: CreateOrganizationInput): Promise<CreateOrganizationResponse> {
    console.log('[Organization API] Creating organization:', input);
    try {
      const response = await apiCall<CreateOrganizationInput, CreateOrganizationResponse>(
        'createOrganization', 
        input
      );
      console.log('[Organization API] Organization created successfully:', response.organization.name);
      return response;
    } catch (error) {
      console.error('[Organization API] Failed to create organization:', error);
      throw error;
    }
  },

  /**
   * Join an existing organization using an invite code
   */
  async joinOrganization(input: JoinOrganizationInput): Promise<JoinOrganizationResponse> {
    console.log('[Organization API] Joining organization with invite code:', input.inviteCode);
    try {
      const response = await apiCall<JoinOrganizationInput, JoinOrganizationResponse>(
        'joinOrganization', 
        input
      );
      console.log('[Organization API] Successfully joined organization:', response.organization.displayName);
      return response;
    } catch (error) {
      console.error('[Organization API] Failed to join organization:', error);
      throw error;
    }
  },

  /**
   * Get the current user's organization
   */
  async getMyOrganization(): Promise<{ organization: Organization; role: string } | null> {
    console.log('[Organization API] Fetching user organization...');
    try {
      const response = await apiCall<void, { organization: Organization; role: string } | null>(
        'getMyOrganization'
      );
      
      if (response?.organization) {
        console.log('[Organization API] Found organization:', response.organization.displayName);
      } else {
        console.log('[Organization API] No organization found for user');
      }
      
      return response;
    } catch (error) {
      console.error('[Organization API] Failed to get user organization:', error);
      throw error;
    }
  },

  /**
   * Create an invite code for the current organization
   * Only admins and owners can create invite codes
   */
  async createInviteCode(input: CreateInviteCodeInput = {}): Promise<CreateInviteCodeResponse> {
    console.log('[Organization API] Creating invite code with options:', input);
    try {
      const response = await apiCall<CreateInviteCodeInput, CreateInviteCodeResponse>(
        'createInviteCode',
        input
      );
      console.log('[Organization API] Invite code created successfully:', response.inviteCode);
      return response;
    } catch (error) {
      console.error('[Organization API] Failed to create invite code:', error);
      throw error;
    }
  },

  /**
   * Get all organizations that the current user belongs to
   * Returns organizations with role and current organization flag
   */
  async getUserOrganizations(): Promise<UserOrganizationMembership[]> {
    console.log('[Organization API] Fetching user organizations...');
    try {
      const response = await apiCall<void, Array<{organization: Organization, role: string}>>('getUserOrganizations');
      console.log('[Organization API] Found', response.length, 'organizations for user');
      
      // Transform to UserOrganizationMembership format with actual roles from backend
      return response.map(item => ({
        organization: item.organization,
        role: item.role as 'owner' | 'admin' | 'member', // Cast to specific role type
        isCurrentOrganization: false // Will be set by context based on current organization
      }));
    } catch (error) {
      console.error('[Organization API] Failed to get user organizations:', error);
      throw error;
    }
  },

  /**
   * Switch the current organization for the user
   * All subsequent API calls will use the new organization context
   */
  async switchCurrentOrganization(input: SwitchOrganizationInput): Promise<SwitchOrganizationResponse> {
    console.log('[Organization API] Switching to organization:', input.organizationId);
    try {
      const response = await apiCall<SwitchOrganizationInput, SwitchOrganizationResponse>(
        'switchCurrentOrganization',
        input
      );
      console.log('[Organization API] Successfully switched to organization:', response.organization.displayName);
      return response;
    } catch (error) {
      console.error('[Organization API] Failed to switch organization:', error);
      throw error;
    }
  }
};

export default organizationService;